import { FunctionComponent } from 'preact';
import { useSelector, useDispatch } from 'react-redux';
import { useEffect, useState } from 'preact/hooks';
import { playerDataSelector } from '../../../common/store/selectors/playerData.selector';
import { Icon } from '../../../common/components/icon/icon';
import { useStyles } from '../../../common/hooks/useStyles';
import { getMiddleScreenPlaylistViewStyleObject } from './middleScreenPlaylistView.style';
import { MORE_VIDEOS_ICON_TYPES } from '../icon/iconMapper';
import { MiddleScreenVideo } from './middleScreenVideo/middleScreenVideo';
import { VideoData } from '../../../common/store/store.types';
import { discoverySelector } from '../../../common/store/selectors/discovery.selector';
import { mediaDataSelector } from '../../../common/store/selectors/mediaData.selector';
import { css } from '../../../common/styles/styles.utils';
import { setInteractionOnEndState, setUserInteraction } from '../store/moreVideos.actions';
import { moreVideosSelector } from '../../../common/store/selectors/moreVideos.selector';
import { splitViewSelector } from '../../../common/store/selectors/splitView.selector';
import { getSmallGalleryLeftArrowId, getSmallGalleryRightArrowId } from '../htmlElements/htmlElementsIdGetter';

const RIGHT_ARIA_LABEL = 'Move to next thumbnail';
const LEFT_ARIA_LABEL = 'Move to previous thumbnail';

interface MiddleScreenPlaylistViewVariablesProps {
  updatedVideoList: Array<VideoData>;
  setExitGalleryView: (value: boolean) => void;
}

export const MiddleScreenPlaylistView: FunctionComponent<MiddleScreenPlaylistViewVariablesProps> = props => {
  const { updatedVideoList, setExitGalleryView } = props;
  const dispatch = useDispatch();
  const showUpNext = useSelector(discoverySelector.showUpNext);
  const videoTagStatus = useSelector(playerDataSelector.videoTagStatus);
  const videoList = useSelector(mediaDataSelector.videoList);
  const interactionOnEndState = useSelector(moreVideosSelector.interactionOnEndState);
  const splitViewRatio = useSelector(splitViewSelector.splitViewRatio);
  const playerUniqueId = useSelector(playerDataSelector.playerInstanceUniqueId);

  const isSingleVideo = videoList ? videoList.length === 1 : false;
  const updatedVideoListLength = updatedVideoList ? updatedVideoList.length : 0;

  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [showRightArrow, setShowRightArrow] = useState(updatedVideoListLength > 1);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [upNextIsShown, setUpNextIsShown] = useState(false);

  const isEndState = isSingleVideo && videoTagStatus === 'complete';
  const upNextOnEndState = showUpNext && isEndState;

  const styles = useStyles(getMiddleScreenPlaylistViewStyleObject, [isEndState, splitViewRatio]);
  const { playlistWrapperStyle, rightArrowStyle, leftArrowStyle } = styles;

  useEffect(() => {
    if (upNextOnEndState) {
      setCurrentVideoIndex(-1);
      setUpNextIsShown(true);
    } if (isEndState && !showUpNext && upNextIsShown) {
      setCurrentVideoIndex(0);
      setUpNextIsShown(false);
    }
  }, [upNextOnEndState, isEndState, upNextIsShown, showUpNext]);

  useEffect(() => {
    if (currentVideoIndex === 0) {
      setShowLeftArrow(false);
    } if (currentVideoIndex === updatedVideoListLength - 1) {
      setShowRightArrow(false);
    }
  }, [currentVideoIndex, updatedVideoListLength]);

  useEffect(() => {
    if (interactionOnEndState) {
      dispatch(setInteractionOnEndState(false));
    }
  }, [interactionOnEndState, dispatch]);

  const onClickLeftArrow = (): void => {
    setShowRightArrow(true);
    setCurrentVideoIndex(currentVideoIndex - 1);
    dispatch(setUserInteraction('moreVideosCarouselClicked'));
    dispatch(setInteractionOnEndState(true));
  };

  const onClickRightArrow = (): void => {
    setShowLeftArrow(true);
    setCurrentVideoIndex(currentVideoIndex + 1);
    dispatch(setUserInteraction('moreVideosCarouselClicked'));
    dispatch(setInteractionOnEndState(true));
  };

  return (
    <div style={css(playlistWrapperStyle)} onClick={(e: MouseEvent): void => e.stopPropagation()}>
      { showLeftArrow
        && (
          <Icon
            icon={MORE_VIDEOS_ICON_TYPES.LEFT_ARROW_CIRCLE_ICON}
            onClick={onClickLeftArrow}
            style={leftArrowStyle}
            id={getSmallGalleryLeftArrowId(playerUniqueId)}
            ariaLabel={LEFT_ARIA_LABEL}
          />
        )}
      { currentVideoIndex !== -1
      && (
        <MiddleScreenVideo
          updatedVideoList={updatedVideoList}
          currentVideoIndex={currentVideoIndex}
          setExitGalleryView={setExitGalleryView}
        />
      )}
      { showRightArrow
        && (
          <Icon
            icon={MORE_VIDEOS_ICON_TYPES.RIGHT_ARROW_CIRCLE_ICON}
            onClick={onClickRightArrow}
            style={rightArrowStyle}
            id={getSmallGalleryRightArrowId(playerUniqueId)}
            ariaLabel={RIGHT_ARIA_LABEL}
          />
        )}
    </div>
  );
};
